<template>
  <div data-component="Lot/Timeline" class="p-4 md:p-8">
    <div v-if="!ready" class="flex justify-center items-center">
      <div class="w-10 h-10 border-4 border-lightgray border-t-blue rounded-full animate-spin"></div>
    </div>
    
    <div v-else-if="allEvents.length === 0" data-component="Folder/Informations-empty" class="text-center py-12 px-6">
      <p class="text-bluegray text-lg mb-6">Aucune visite ou pré-chiffrage n'a encore été effectué pour ce lot</p>
      <router-link 
        v-if="isVisitable"
        :to="`/dossier/${propertyId}/nouvelle-visite`"
        class="nouvelle-visite-button"
      >
        Nouvelle visite
      </router-link>
    </div>
    
    <div v-else>

      <!-- Section Pré-chiffrages -->
      <div v-if="preChiffrageEvents.length > 0" class="mb-8">
        <h2 class="text-[#5B7A93] font-semibold text-xl mb-4 border-b border-[#D8E6EF] pb-2">Pré-chiffrage</h2>
        <div
          v-for="event in preChiffrageEvents"
          :key="`event-pc-${event.id}`"
          class="mb-6"
        >
          <h3 class="text-[#9DB3C4] font-medium">
            {{ formatDate(event.createdAt) }}
            &nbsp;&nbsp;<span class="font-bold">.</span>&nbsp;&nbsp;
            <small class="text-[#9DB3C4] text-base font-normal">par <strong class="text-[#9DB3C4] font-bold">{{ event.author }}</strong></small>
          </h3>

          <div class="flex mt-2 items-stretch gap-2">
            <!-- Carte principale -->
            <div class="pc-card flex-1" :class="event.isGpr ? 'pc-card--gpr' : 'pc-card--cat'">
              <!-- Ligne compacte : type + titre + stats + badges + toggle -->
              <div class="pc-card__row">
                <span class="pc-type-badge" :class="event.isGpr ? 'pc-type-badge--gpr' : 'pc-type-badge--cat'">
                  <i :class="event.isGpr ? 'fas fa-bolt' : 'fas fa-list-ul'" class="mr-1"></i>
                  {{ event.isGpr ? 'GPR — EDF' : 'CAT' }}
                </span>
                <span class="pc-card__title">Pré-chiffrage</span>
                <span v-if="event.isGpr && event.coutTotalGPR" class="pc-stat"><i class="fas fa-euro-sign mr-1"></i>{{ event.coutTotalGPR }} €</span>
                <span v-if="event.isGpr && event.dureePrevTravauxGPR" class="pc-stat"><i class="fas fa-clock mr-1"></i>{{ event.dureePrevTravauxGPR }} j</span>
                <span v-if="!event.isGpr && event.lineCount" class="pc-stat"><i class="fas fa-list mr-1"></i>{{ event.lineCount }} ligne{{ event.lineCount > 1 ? 's' : '' }}</span>
                <span v-if="event.chiffrageTermine === 'Oui'" class="pc-badge pc-badge--success">Terminé</span>
                <span v-if="event.isSinistre === 'Oui'" class="pc-badge pc-badge--warning">Sinistre</span>
                <span v-if="event.photosEnregistrees === 'Oui'" class="pc-badge pc-badge--info">Photos</span>
                <span v-if="event.isGpr && event.travauxGPRValidesEDF" class="pc-badge"
                  :class="event.travauxGPRValidesEDF === 'OUI' ? 'pc-badge--edf-ok' : 'pc-badge--edf-ko'"
                ><i class="fas fa-bolt mr-1"></i>EDF : {{ event.travauxGPRValidesEDF }}</span>
                <button type="button" class="pc-toggle-btn ml-auto" @click="togglePreview(event.id)">
                  <i :class="expandedPreviews[event.id] ? 'fas fa-chevron-up' : 'fas fa-eye'" class="mr-1"></i>
                  {{ expandedPreviews[event.id] ? "Masquer" : 'Aperçu' }}
                </button>
              </div>
              <!-- Aperçu des travaux (dépliable) -->
              <div v-if="expandedPreviews[event.id]" class="pc-card__preview">
                <template v-if="event.lines && event.lines.length > 0">
                  <div class="pc-preview__section">
                    <h5 class="pc-preview__section-title">
                      <span class="pc-preview__section-badge" :class="event.isGpr ? 'pc-preview__section-badge--gpr' : 'pc-preview__section-badge--cat'">
                        {{ event.isGpr ? 'GPR' : 'CAT' }}
                      </span>
                      Travaux {{ event.isGpr ? 'GPR (EDF)' : 'CAT' }}
                    </h5>
                    <div class="pc-lines-list">
                      <div v-for="(line, idx) in event.lines" :key="idx"
                        class="pc-line-row" :class="event.isGpr ? 'pc-line-row--gpr' : 'pc-line-row--cat'">
                        <span class="pc-line-num" :class="event.isGpr ? 'pc-line-num--gpr' : ''">{{ idx + 1 }}</span>
                        <div class="pc-line-content">
                          <template v-if="!event.isGpr">
                            <span class="pc-line-chip"><i class="fas fa-door-open"></i> {{ line.roomType || '—' }}</span>
                            <span v-if="line.workType" class="pc-line-chip"><i class="fas fa-tools"></i> {{ line.workType }}</span>
                            <span v-if="line.sinistre === 'oui'" class="pc-line-chip pc-line-chip--warning"><i class="fas fa-exclamation-triangle"></i> Sinistre</span>
                          </template>
                          <template v-else>
                            <span v-if="line.precoTravauxGPR" class="pc-line-chip"><i class="fas fa-hard-hat"></i> {{ line.precoTravauxGPR }}</span>
                            <span v-if="line.coutTravauxGPR != null" class="pc-line-chip"><i class="fas fa-euro-sign"></i> {{ line.coutTravauxGPR }} €</span>
                            <span v-if="line.dureePrevTravauxGPR != null" class="pc-line-chip"><i class="fas fa-clock"></i> {{ line.dureePrevTravauxGPR }} j</span>
                            <span v-if="line.travauxGPRValidesEDF" class="pc-line-chip"
                              :class="line.travauxGPRValidesEDF === 'OUI' ? 'pc-line-chip--success' : 'pc-line-chip--warning'"
                            ><i class="fas fa-bolt"></i> EDF : {{ line.travauxGPRValidesEDF }}</span>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <div v-else class="pc-preview__empty">
                  <i class="fas fa-info-circle mr-2"></i>Aucune ligne de travaux enregistrée
                </div>
                <div v-if="event.commentairesGeneraux" class="pc-preview__comments">
                  <strong>Commentaires :</strong> {{ event.commentairesGeneraux }}
                </div>
              </div>
            </div>

            <!-- Bouton PDF -->
            <button v-if="isOnline" type="button"
              @click="downloadPreChiffragePdf(event.id)"
              :disabled="downloadingVisits[event.id]"
              data-component="Widget/Button"
              class="button flex-shrink-0"
              style="min-width: 120px; height: auto; font-size: 1.4rem; align-items: center; justify-content: center;"
            >
              <i :class="['fas', downloadingVisits[event.id] ? 'fa-spinner fa-spin' : 'fa-download', 'mr-2']"></i>
              {{ downloadingVisits[event.id] ? '...' : 'PDF' }}
            </button>

            <!-- Bouton Voir la fiche -->
            <router-link :to="`/dossier/${propertyId}/pre-chiffrage/${event.id}`"
              data-component="Widget/Button"
              class="button flex-shrink-0"
              :class="event.isGpr ? 'button--gpr' : ''"
              style="min-width: 180px; height: auto; font-size: 1.5rem; align-items: center; justify-content: center;"
            >
              <i class="ico document mr-4"></i> Voir la fiche
            </router-link>
          </div>
        </div>
      </div>

      <!-- Section Visites -->
      <div v-if="visitEvents.length > 0" class="mb-8">
        <h2 class="text-[#5B7A93] font-semibold text-xl mb-4 border-b border-[#D8E6EF] pb-2">Visites</h2>
        <div
          v-for="event in visitEvents"
          :key="`event-visit-${event.id}`"
          class="mb-6"
        >
          <h3 class="text-[#9DB3C4] font-medium">
            {{ formatDate(event.createdAt) }}
            &nbsp;&nbsp;<span class="font-bold">.</span>&nbsp;&nbsp;
            <small class="text-[#9DB3C4] text-base font-normal">par <strong class="text-[#9DB3C4] font-bold">{{ event.author }}</strong></small>
          </h3>

          <div class="flex mt-2 items-stretch gap-2">
            <div class="flex flex-1 justify-between p-4 mb-0 bg-white rounded shadow relative">
              <div v-if="event.pending" class="absolute top-4 right-4 inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                </svg>
                En attente de synchronisation
              </div>
              <div>
                <p class="text-black font-bold text-base">Fiche visite</p>
                <p class="text-bluegray text-base font-bold"><small>{{ event.message }}</small></p>
              </div>
            </div>

            <button
              v-if="isOnline"
              @click="downloadPdf(event.visit)"
              :disabled="downloadingVisits[event.visit]"
              data-component="Widget/Button"
              class="button flex-shrink-0"
              style="min-width: 200px; height: auto; font-size: 1.5rem; align-items: center; justify-content: center;"
            >
              <i :class="['fas', downloadingVisits[event.visit] ? 'fa-spinner fa-spin' : 'fa-download', 'mr-2']"></i>
              {{ downloadingVisits[event.visit] ? 'Téléchargement...' : 'Télécharger PDF' }}
            </button>

            <router-link
              :to="`/dossier/${propertyId}/fiche/${event.visit}`"
              data-component="Widget/Button"
              class="button flex-shrink-0"
              style="min-width: 180px; height: auto; font-size: 1.5rem; align-items: center; justify-content: center;"
            >
              <i class="ico document mr-4"></i> Voir la fiche
            </router-link>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import api from '../../services/api'
import authService from '../../services/authService'
import apiStatusService from '../../services/apiStatusService' // conservé pour getStatus()


export default {
  name: 'TimelineView',
  components: {},
  data() {
    return {
      downloadingVisits: {},
      expandedPreviews: {},
      isApiOnline: false,
      removeApiListener: null,
      preChiffrages: []
    }
  },
  computed: {
    ...mapState('timeline', ['events', 'loading', 'error']),
    ...mapState('lot', ['currentLot']),
    
    propertyId() {
      return this.$route.params.id
    },
    
    timelineEvents() {
      return this.events
    },
    
    property() {
      return this.currentLot
    },
    
    ready() {
      return !this.loading
    },
    
    isVisitable() {
      // Vérifier si la propriété est visitable (basé sur scheduledVisitStatus)
      if (!this.property) return false
      const status = this.property.scheduledVisitStatus
      return status !== null && status !== undefined
    },
    
    isOnline() {
      return this.isApiOnline
    },

    preChiffrageEvents() {
      return this.allEvents.filter(e => e.type === 'prechiffrage')
    },

    visitEvents() {
      return this.allEvents.filter(e => e.type === 'visit')
    },
    
    // Fusionner les visites et les pré-chiffrages dans une seule timeline
    allEvents() {
      const combinedEvents = []
      
      // Ajouter les visites
      this.timelineEvents.forEach(event => {
        combinedEvents.push({
          ...event,
          type: 'visit'
        })
      })
      
      // Ajouter les pré-chiffrages
      this.preChiffrages.forEach(pc => {
        combinedEvents.push({
          id: pc.id,
          type: 'prechiffrage',
          createdAt: pc.createdAt,
          updatedAt: pc.updatedAt,
          author: pc.author || 'Inconnu',
          isGpr: pc.isGpr || false,
          // GPR fields
          precoTravauxGPR: pc.precoTravauxGPR,
          dureePrevTravauxGPR: pc.dureePrevTravauxGPR,
          coutTravauxGPR: pc.coutTravauxGPR,
          coutDAATGPR: pc.coutDAATGPR,
          coutDiagElecGPR: pc.coutDiagElecGPR,
          coutTotalGPR: pc.coutTotalGPR,
          travauxGPRValidesEDF: pc.travauxGPRValidesEDF,
          // General fields
          isSinistre: pc.isSinistre,
          commentairesSinistre: pc.commentairesSinistre,
          commentairesGeneraux: pc.commentairesGeneraux,
          chiffrageTermine: pc.chiffrageTermine,
          photosEnregistrees: pc.photosEnregistrees,
          // Lines for CAT mode
          lineCount: pc.lineCount || 0,
          lines: pc.lines || [],
          showDetails: false
        })
      })
      
      // Trier par date de création (plus récent en premier)
      combinedEvents.sort((a, b) => {
        const dateA = new Date(a.createdAt)
        const dateB = new Date(b.createdAt)
        return dateB - dateA
      })
      
      return combinedEvents
    }
  },
  async mounted() {
    // Initialiser le statut de l'API (valeur courante déjà connue)
    this.isApiOnline = apiStatusService.getStatus()

    // S'abonner aux changements de statut via le bus global
    this.removeApiListener = Event.listen('api-status-change', ({ isAvailable }) => {
      this.isApiOnline = isAvailable
    })
    
    await this.loadData()
  },
  beforeUnmount() {
    // Nettoyer le listener quand le composant est détruit
    if (this.removeApiListener) {
      this.removeApiListener()
    }
  },
  methods: {
    ...mapActions('timeline', ['fetchTimelineEvents']),
    ...mapActions('lot', ['fetchLot']),
    
    async loadData() {
      try {
        await Promise.all([
          this.fetchLot(this.propertyId),
          this.fetchTimelineEvents(this.propertyId),
          this.fetchPreChiffrages()
        ])
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
      }
    },
    
    async fetchPreChiffrages() {
      try {
        // Importer le service Dexie
        const dexieService = (await import('../../services/dexieService')).default
        
        // Récupérer la propriété depuis IndexedDB
        const property = await dexieService.getProperty(this.propertyId)
        
        if (!property) {
          console.warn('Propriété non trouvée')
          this.preChiffrages = []
          return
        }
        
        // Récupérer les pré-chiffrages de la propriété
        let preChiffragesData = []
        
        if (property.preChiffrages && Array.isArray(property.preChiffrages) && property.preChiffrages.length > 0) {
          preChiffragesData = property.preChiffrages
        }
        
        // Mapper les pré-chiffrages avec l'auteur si disponible
        this.preChiffrages = preChiffragesData.map(pc => ({
          id: pc.id,
          createdAt: pc.createdAt,
          updatedAt: pc.updatedAt,
          author: pc.creator?.fullName || pc.creator?.email || 'Inconnu',
          isGpr: pc.isGpr || false,
          // GPR fields
          precoTravauxGPR: pc.precoTravauxGPR,
          dureePrevTravauxGPR: pc.dureePrevTravauxGPR,
          coutTravauxGPR: pc.coutTravauxGPR,
          coutDAATGPR: pc.coutDAATGPR,
          coutDiagElecGPR: pc.coutDiagElecGPR,
          coutTotalGPR: pc.coutTotalGPR,
          travauxGPRValidesEDF: pc.travauxGPRValidesEDF,
          // General fields
          isSinistre: pc.isSinistre,
          commentairesSinistre: pc.commentairesSinistre,
          commentairesGeneraux: pc.commentairesGeneraux,
          chiffrageTermine: pc.chiffrageTermine,
          photosEnregistrees: pc.photosEnregistrees,
          // Lines for CAT/GPR mode
          lineCount: pc.lines ? pc.lines.length : 0,
          lines: pc.lines || []
        }))
        
      } catch (error) {
        console.error('Erreur lors du chargement des pré-chiffrages:', error)
        this.preChiffrages = []
      }
    },
    
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY')
    },

    togglePreview(eventId) {
      this.expandedPreviews = {
        ...this.expandedPreviews,
        [eventId]: !this.expandedPreviews[eventId]
      }
    },

    async downloadPdf(visitId) {
      // Vérifier si le téléchargement n'est pas déjà en cours
      if (this.downloadingVisits[visitId]) {
        return
      }
      
      try {
        // Marquer le téléchargement comme en cours
        this.downloadingVisits[visitId] = true
        
        // Construire l'URL de téléchargement
        const downloadUrl = `${api.baseURL}/pdf/visits/${visitId}/download`
        
        // Faire la requête de téléchargement avec authentification
        const response = await authService.makeAuthenticatedRequest(downloadUrl)
        
        if (!response.ok) {
          throw new Error(`Erreur lors du téléchargement: ${response.status}`)
        }
        
        // Récupérer le blob du fichier PDF
        const blob = await response.blob()
        
        // Créer un lien de téléchargement
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        
        // Générer un nom de fichier avec la date
        const dateStr = moment().format('YYYY-MM-DD')
        link.download = `visite-${visitId}-${dateStr}.pdf`
        
        // Déclencher le téléchargement
        document.body.appendChild(link)
        link.click()
        
        // Nettoyer
        window.URL.revokeObjectURL(url)
        document.body.removeChild(link)
        
      } catch (error) {
        console.error('Erreur lors du téléchargement du PDF:', error)
        alert('Erreur lors du téléchargement du PDF. Veuillez réessayer.')
      } finally {
        // Marquer le téléchargement comme terminé
        this.downloadingVisits[visitId] = false
      }
    },
    
    async downloadPreChiffragePdf(preChiffrageId) {
      // Vérifier si le téléchargement n'est pas déjà en cours
      if (this.downloadingVisits[preChiffrageId]) {
        return
      }
      
      try {
        // Marquer le téléchargement comme en cours
        this.downloadingVisits[preChiffrageId] = true
        
        // Construire l'URL de téléchargement
        const downloadUrl = `${api.baseURL}/pre-chiffrages/${preChiffrageId}/pdf`
        
        // Faire la requête de téléchargement avec authentification
        const response = await authService.makeAuthenticatedRequest(downloadUrl)
        
        if (!response.ok) {
          throw new Error(`Erreur lors du téléchargement: ${response.status}`)
        }
        
        // Récupérer le blob du fichier PDF
        const blob = await response.blob()
        
        // Créer un lien de téléchargement
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        
        // Extraire le nom du fichier depuis les headers (si disponible) ou utiliser un nom par défaut
        const contentDisposition = response.headers.get('Content-Disposition')
        let filename = `pre-chiffrage-${preChiffrageId}.pdf`
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1]
          }
        }
        
        link.download = filename
        
        // Déclencher le téléchargement
        document.body.appendChild(link)
        link.click()
        
        // Nettoyer
        window.URL.revokeObjectURL(url)
        document.body.removeChild(link)
        
      } catch (error) {
        console.error('Erreur lors du téléchargement du PDF du pré-chiffrage:', error)
        alert('Erreur lors du téléchargement du PDF. Veuillez réessayer.')
      } finally {
        // Marquer le téléchargement comme terminé
        this.downloadingVisits[preChiffrageId] = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import './Timeline.scss';
</style>
